import { parse as parseQueryParams } from 'querystring';
import { MarketingPayload } from '~/types/RegisterUser';

/**
 * The platform (or vendor) where the traffic originates, like Facebook
 * or your email newsletter.
 */
type UTMSource = string;

/**
 * You can use this to identify the medium like Cost Per Click (CPC),
 * social media, affiliate or QR code.
 */
type UTMMedium = string;

/**
 * This is just to identify your campaign. Like your website or a
 * specific product promotion.
 */
type UTMCampaign = string;

/**
 * You’ll use this mainly for tracking your keywords during a paid AdWords
 * campaign. You can also use it in your display ad campaigns to identify
 * aspects of your audience.
 */
type UTMTerm = string;

/**
 * If you’re A/B testing ads, then this is a useful metric that passes
 * details about your ad. You can also use it to differentiate links
 * that point to the same URL.
 */
type UTMContent = string;

/**
 * UTM parameters as described by Google analytics. Terms and content are the
 * only optional parameters.
 *
 * See: https://neilpatel.com/blog/the-ultimate-guide-to-using-utm-parameters/
 * See: https://support.google.com/analytics/answer/1033863#parameters
 */
export interface RawUTMParams {
  utm_source: UTMSource;
  utm_medium: UTMMedium;
  utm_campaign: UTMCampaign;
  utm_term?: UTMTerm;
  utm_content?: UTMContent;
}

/**
 * Normalized version of UTM parameters as described by Google analytics. Terms
 * and content are the only optional parameters.
 *
 * See: https://neilpatel.com/blog/the-ultimate-guide-to-using-utm-parameters/
 * See: https://support.google.com/analytics/answer/1033863#parameters
 */
export type UTMParams = {
  source: UTMSource;
  medium: UTMMedium;
  campaign: UTMCampaign;
  term: UTMTerm;
  content: UTMContent;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizeUTMParameters(rawParams: any): UTMParams {
  const { utm_source, utm_medium, utm_campaign, utm_term, utm_content }: RawUTMParams = rawParams;
  return {
    source: decodeURIComponent(utm_source || ''),
    medium: decodeURIComponent(utm_medium || ''),
    campaign: decodeURIComponent(utm_campaign || ''),
    term: decodeURIComponent(utm_term || ''),
    content: decodeURIComponent(utm_content || ''),
  };
}

/**
 * Takes a url and extracts out the query string or "search" string.
 *
 * @function extractQueryStringFromURL
 * @param {string} url
 * @private
 */
function extractQueryStringFromURL(url: string): string {
  let querystring = '';

  if (window && window.URL) {
    const urlObj = new window.URL(url);
    querystring = urlObj.search.replace(/^\?/, '');
  } else if (window && window.document) {
    const anchor = window.document.createElement('a');
    querystring = anchor.search.replace(/^\?/, '');
  } else {
    // NOTE: this is not a surefire way to ensure queryparams
    // but should only be triggered in rare cases.
    querystring = url.replace(/.*?\?/, '');
  }

  return querystring;
}

/**
 * Extracts the UTM parameters from the url
 *
 * @function extractUTMParameters
 * @param {string} url
 * @public
 */
export function extractUTMParameters(url: string) {
  const querystring = extractQueryStringFromURL(url);
  const queryParams = parseQueryParams(querystring);
  return normalizeUTMParameters(queryParams);
}

const SHOPIFY_REGEX = /shopify/gi;

/**
 * Returns a boolean for if the UTM params signal a referral from Shopify
 *
 * @function isReferredByShopify
 * @param {object} queryParams - an object that has the standard UTM query params
 * @returns {boolean} Boolean
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isReferredByShopify(queryParams: any): boolean {
  const utm: UTMParams = normalizeUTMParameters(queryParams);
  return [utm.source, utm.content].some((utmParam) => utmParam.search(SHOPIFY_REGEX) !== -1);
}

export function extractMarketingPayloadFromCookie(cookie): MarketingPayload {
  return {
    source: cookie.utm_source || '',
    campaign: cookie.utm_campaign || '',
    campaignTerm: cookie.utm_term || '',
    campaignContent: cookie.utm_content || '',
    medium: cookie.utm_medium || '',
    attributionSnippet: cookie.attribution_snippet || '',
    shDevice: cookie.sh_device || '',
    shMatchtype: cookie.sh_matchtype || '',
    shAd1: cookie.sh_ad1 || '',
    shAd2: cookie.sh_ad2 || '',
    shAd3: cookie.sh_ad3 || '',
    shAd4: cookie.sh_ad4 || '',
    httpReferrer: cookie.http_referrer || '',
    rawHttpReferrer: document.referrer,
    rawQueryString: document.location ? document.location.search : undefined,
  };
}

export default {
  isReferredByShopify,
  extractMarketingPayloadFromCookie,
  normalizeParameters: normalizeUTMParameters,
  extractParameters: extractUTMParameters,
};
